import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import PageLayout from "@/components/feature/PageLayout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import MovieCard from "@/components/feature/MovieCard";
import ShortVideoCard from "@/components/feature/ShortVideoCard";
import EmptyState from "@/components/feature/EmptyState";
import { movies } from "@/mocks/movies";
import { shortVideos } from "@/mocks/shortVideos";
import { usePageMeta } from "@/hooks/usePageMeta";

type SearchTab = "all" | "movie" | "short";

const tabs: { value: SearchTab; label: string; icon: string }[] = [
  { value: "all", label: "全部结果", icon: "ri-apps-2-line" },
  { value: "movie", label: "电影", icon: "ri-film-line" },
  { value: "short", label: "短视频", icon: "ri-play-circle-line" },
];

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") ?? "";
  const [keyword, setKeyword] = useState(query);
  const [tab, setTab] = useState<SearchTab>("all");

  useEffect(() => {
    setKeyword(query);
  }, [query]);

  usePageMeta(
    query ? `搜索“${query}”结果 - 午夜视频` : "全站搜索 - 午夜视频",
    query
      ? `午夜视频全站搜索“${query}”的结果，包含相关午夜电影与精选短视频，可按电影或短视频类型切换查看。`
      : "在午夜视频中搜索午夜电影、精选短视频、导演与类型关键词，快速定位想看的内容。",
    "午夜视频搜索,搜索午夜电影,搜索短视频,午夜影视检索",
  );

  const normalized = query.trim().toLowerCase();

  const matchedMovies = useMemo(() => {
    if (!normalized) return [];
    return movies.filter((movie) => {
      const haystack = [
        movie.title,
        movie.genre,
        movie.region,
        movie.director,
        movie.cast,
        String(movie.year),
        movie.synopsis,
        ...movie.tags,
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(normalized);
    });
  }, [normalized]);

  const matchedShorts = useMemo(() => {
    if (!normalized) return [];
    return shortVideos.filter((video) => {
      const haystack = [video.title, video.tag, video.creator, video.intro]
        .join(" ")
        .toLowerCase();
      return haystack.includes(normalized);
    });
  }, [normalized]);

  const total = matchedMovies.length + matchedShorts.length;
  const showMovies = (tab === "all" || tab === "movie") && matchedMovies.length > 0;
  const showShorts = (tab === "all" || tab === "short") && matchedShorts.length > 0;

  const handleChange = (value: string) => {
    setKeyword(value);
    if (value.trim()) {
      setSearchParams({ q: value.trim() }, { replace: true });
    } else {
      setSearchParams({}, { replace: true });
    }
  };

  return (
    <PageLayout>
      <div className="w-full px-4 py-8 md:px-8 md:py-10">
        <Breadcrumb items={[{ label: "搜索结果" }]} />

        <p className="flex items-center gap-2 font-label text-xs tracking-[0.3em] text-accent-500">
          <span className="inline-block h-px w-6 bg-accent-500/70" />
          SEARCH
        </p>
        <h1 className="mt-3 font-heading text-2xl font-bold tracking-wide text-foreground-50 md:text-3xl">
          {query ? `搜索“${query}”的结果` : "全站搜索"}
        </h1>
        <p className="mt-3 max-w-2xl text-sm leading-7 text-foreground-400">
          输入片名、类型、地区或导演姓名，即可同时检索午夜电影与精选短视频。关键词变化时结果会立即更新。
        </p>

        <div className="mt-6 rounded-lg border border-background-800 bg-background-900/70 p-4 md:p-5">
          <div className="flex h-12 items-center gap-3 rounded-md border border-background-700 bg-background-950 px-4 transition-colors focus-within:border-accent-500">
            <i className="ri-search-line text-lg text-foreground-500" />
            <input
              type="text"
              value={keyword}
              onChange={(event) => handleChange(event.target.value)}
              placeholder="例如：悬疑、雨夜迷踪、夜航影像"
              aria-label="搜索关键词"
              className="w-full bg-transparent text-sm text-foreground-100 outline-none placeholder:text-foreground-500"
            />
            {keyword && (
              <button
                type="button"
                onClick={() => handleChange("")}
                aria-label="清空关键词"
                className="flex h-7 w-7 items-center justify-center rounded-full text-foreground-500 transition-colors hover:bg-background-800 hover:text-foreground-100"
              >
                <i className="ri-close-line text-base" />
              </button>
            )}
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-1 rounded-full border border-background-700 bg-background-950 p-1">
              {tabs.map((item) => (
                <button
                  key={item.value}
                  type="button"
                  onClick={() => setTab(item.value)}
                  className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs transition-colors ${
                    tab === item.value
                      ? "bg-accent-500 font-medium text-background-950"
                      : "text-foreground-400 hover:text-foreground-100"
                  }`}
                >
                  <i className={`${item.icon} text-sm`} />
                  {item.label}
                </button>
              ))}
            </div>
            <p className="text-xs text-foreground-500">
              {query ? (
                <>
                  关于“<span className="text-accent-400">{query}</span>”共找到{" "}
                  <span className="text-accent-400">{total}</span> 条结果
                  {total > 0 && `（电影 ${matchedMovies.length} · 短视频 ${matchedShorts.length}）`}
                </>
              ) : (
                "输入关键词开始搜索"
              )}
            </p>
          </div>
        </div>

        {!query && (
          <div className="mt-6 rounded-lg border border-background-800 bg-background-900/50 p-6">
            <h2 className="flex items-center gap-2 font-heading text-sm font-semibold text-foreground-100">
              <i className="ri-fire-line text-base text-accent-500" />
              大家都在搜
            </h2>
            <div className="mt-4 flex flex-wrap gap-2">
              {["悬疑", "恐怖", "雨夜迷踪", "深夜厨房", "霓虹", "剪辑"].map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => handleChange(item)}
                  className="rounded-full border border-background-700 bg-background-950 px-3.5 py-1.5 text-xs text-foreground-300 transition-colors hover:border-accent-500/60 hover:text-accent-400"
                >
                  {item}
                </button>
              ))}
            </div>
          </div>
        )}

        {query && total === 0 && (
          <div className="mt-6">
            <EmptyState
              icon="ri-search-eye-line"
              title={`没有找到与“${query}”相关的内容`}
              description="可以试试更短的关键词，比如只输入类型或片名中的两个字；也可以直接浏览电影分类和短视频分类。"
              actionLabel="返回首页"
              actionTo="/"
            />
            <div className="mt-4 flex flex-wrap items-center justify-center gap-3 text-xs">
              <Link
                to="/movies"
                className="rounded-md border border-background-700 px-3.5 py-2 text-foreground-300 transition-colors hover:border-accent-500/60 hover:text-accent-400"
              >
                浏览电影分类
              </Link>
              <Link
                to="/short-videos"
                className="rounded-md border border-background-700 px-3.5 py-2 text-foreground-300 transition-colors hover:border-accent-500/60 hover:text-accent-400"
              >
                浏览短视频分类
              </Link>
            </div>
          </div>
        )}

        {showMovies && (
          <section className="mt-8">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="flex items-center gap-2 font-heading text-lg font-semibold text-foreground-50">
                <i className="ri-film-line text-base text-accent-500" />
                相关电影
                <span className="text-xs font-normal text-foreground-500">
                  {matchedMovies.length} 部
                </span>
              </h2>
              {tab === "all" && matchedMovies.length > 8 && (
                <button
                  type="button"
                  onClick={() => setTab("movie")}
                  className="text-xs text-foreground-500 transition-colors hover:text-accent-400"
                >
                  只看电影
                </button>
              )}
            </div>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-2 md:gap-5 lg:grid-cols-4">
              {(tab === "all" ? matchedMovies.slice(0, 8) : matchedMovies).map((movie) => (
                <MovieCard
                  key={movie.id}
                  id={movie.id}
                  title={movie.title}
                  year={movie.year}
                  genre={movie.genre}
                  region={movie.region}
                  director={movie.director}
                  duration={movie.duration}
                  poster={movie.poster}
                />
              ))}
            </div>
          </section>
        )}

        {showShorts && (
          <section className="mt-10">
            <div className="mb-4 flex items-center justify-between">
              <h2 className="flex items-center gap-2 font-heading text-lg font-semibold text-foreground-50">
                <i className="ri-play-circle-line text-base text-accent-500" />
                相关短视频
                <span className="text-xs font-normal text-foreground-500">
                  {matchedShorts.length} 条
                </span>
              </h2>
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:gap-5 lg:grid-cols-4">
              {(tab === "all" ? matchedShorts.slice(0, 4) : matchedShorts).map((video) => (
                <ShortVideoCard
                  key={video.id}
                  id={video.id}
                  title={video.title}
                  tag={video.tag}
                  duration={video.duration}
                  views={video.views}
                  cover={video.cover}
                  uploadDate={video.uploadDate}
                />
              ))}
            </div>
          </section>
        )}
      </div>
    </PageLayout>
  );
}